<script setup lang="ts">
import type { Component } from 'vue'
import { computed, ref } from 'vue'
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from 'reka-ui'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { ChevronRight } from 'lucide-vue-next'
import SidebarMenuButton from './SidebarMenuButton.vue'
import SidebarMenuItem from './SidebarMenuItem.vue'
import { useSidebar } from './utils'

defineProps<{
    icon: Component
    label: string
    defaultOpen?: boolean
}>()

const { state, isMobile } = useSidebar()

const isCollapsed = computed(() => state.value === 'collapsed' && !isMobile.value)
const popoverOpen = ref(false)
</script>

<template>
    <!-- Popover mode when sidebar is collapsed -->
    <SidebarMenuItem v-if="isCollapsed">
        <Popover v-model:open="popoverOpen">
            <PopoverTrigger as-child>
                <SidebarMenuButton :tooltip="label">
                    <component :is="icon" />
                    <span>{{ label }}</span>
                </SidebarMenuButton>
            </PopoverTrigger>
            <PopoverContent
                side="right"
                align="start"
                :side-offset="8"
                class="min-w-44 p-1"
                @click="popoverOpen = false"
            >
                <slot name="items" />
            </PopoverContent>
        </Popover>
    </SidebarMenuItem>

    <!-- Collapsible mode when sidebar is expanded -->
    <CollapsibleRoot v-else as-child :default-open="defaultOpen">
        <SidebarMenuItem>
            <CollapsibleTrigger as-child>
                <SidebarMenuButton>
                    <component :is="icon" />
                    <span>{{ label }}</span>
                    <ChevronRight
                        class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                    />
                </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent class="group/collapsible">
                <slot name="collapsible" />
            </CollapsibleContent>
        </SidebarMenuItem>
    </CollapsibleRoot>
</template>
